import { Fragment, useCallback, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify";
import "../assets/qreader.css";
import useDialogStore from "../stores/useDialogStore";
import { useQueryClient } from "@tanstack/react-query";

export default function InvoiceQRScanner({
  invoiceNumber,
  onClose,
  shouldCloseButton = false,
}) {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);

  // Smooth progress animation
  useEffect(() => {
    if (isScanning) {
      setProgress(0);
      const startTime = Date.now();
      const duration = 1700; // 1.7 seconds

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const newProgress = Math.min((elapsed / duration) * 100, 100);
        setProgress(newProgress);

        if (newProgress < 100) {
          requestAnimationFrame(updateProgress);
        } else {
          setIsScanning(false);
          setProgress(0);
        }
      };

      requestAnimationFrame(updateProgress);
    }
  }, [isScanning]);

  const handleResult = useCallback(
    async (text, result) => {
      if (isScanning) return;

      setIsScanning(true);
      if (!result.text || !invoiceNumber) {
        toast.error("Invalid scan data or invoice number");
        return;
      }
      try {
        const response = await axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/device-statuses/verify-device`,
          {
            unique_id: result.text,
            invoice_number: invoiceNumber.toString(),
          }
        );
        if (response.data.device_status) {
          toast.success(
            response.data.message || "Device verified successfully!"
          );
        }
      } catch (error) {
        if (error.response?.status === 404) {
          toast.error(
            <span style={{ fontWeight: "bold" }}>
              {error.response.data.error}
              <br />
              <span style={{ fontSize: "0.9em" }}>
                {error.response.data.details}
              </span>
            </span>,
            { autoClose: false }
          );
        } else {
          toast.error(
            error.response?.data?.error ||
              "Failed to verify device. Please try again.",
            { autoClose: 3000 }
          );
        }
      }
    },
    [isScanning, invoiceNumber, onClose]
  );

  const handleError = useCallback((error) => {
    toast.error("Scanner error: " + error?.message, { autoClose: 3000 });
    console.error("Scanner error:", error);
  }, []);

  return (
    <div className="qreaderr">
      <Dialog.Title className="text-sm font-medium text-gray-900 mb-4">
        Scan Device QR Code to Verify Against Invoice #{invoiceNumber}
      </Dialog.Title>

      <button
        onClick={() => setIsScanning(true)}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
      >
        Test Scanning State
      </button>

      <div className="relative w-full">
        <div className="relative w-full aspect-square">
          <Scanner
            onResult={handleResult}
            onError={handleError}
            constraints={{
              facingMode: "environment",
              audio: false, // Disable audio to prevent beeping
            }}
            className="w-full h-full rounded-lg"
          />

          {/* Smooth overlay when scanning */}
          {isScanning && (
            <div className="absolute inset-0 bg-black bg-opacity-70 rounded-lg flex flex-col items-center justify-center z-10 transition-opacity duration-300">
              {/* Remaining time */}
              <div className="text-4xl font-bold text-white mb-6 select-none">
                {(1.7 - (progress / 100) * 1.7).toFixed(1)}s
              </div>

              {/* Smooth progress bar */}
              <div className="w-64 h-3 bg-gray-700 rounded-full overflow-hidden mb-6">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-100 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Smooth text */}
              <div className="text-base text-white font-medium select-none">
                Please wait before next scan...
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-500">
        {isScanning
          ? "Please wait before scanning the next QR code"
          : "Please align the QR code within the scanner frame"}
      </div>
    </div>
  );
}
